import { AxiosResponse } from 'axios';

import { axios } from '../../base/bootstrap';

import { GetAllCasinoGameCategoryResponse } from '../../casino/Api/CRM/Responses/CasinoGameCategoryResponse';
import { GetAllCasinoGameCategoryBlockResponse } from '../../casino/Api/CRM/Responses/GetAllCasinoGameCategoryBlockResponse';
import { Lootbox, LootboxWinEntry } from '../../components/lootbox/Types';
import AbstractSiteApi from '../AbstractSiteApi';
import GetFaqResponse from '../CRM/Responses/FaqResponse';
import { GoogleMap } from '../Models/GoogleMap';
import Bundle from '../Models/SiteApi/Bundle';
import { SiteDefaultLottery } from '../Models/SiteApi/SiteDefaultLottery';
import SiteFaq from '../Models/SiteApi/SiteFaq';
import SiteFaqCategory from '../Models/SiteApi/SiteFaqCategory';

import SliderItem from '../Models/SliderItem';

import LotteryListResponse from './Response/LotteryListResponse';
import { GetSliderItems } from './Response/Sliders';
import {
    GetHeaderMenuItemsResponse,
    GetJackpotBannerDataResponse,
    GetLatestDrawResponse,
    GetLatestWinnersResponse,
    LootboxDataResponse,
    LootboxOpenResponse,
    LootboxRedeemResponse,
    PlayResponse,
    GetLotteryListWithRules,
    GetLotteryForSingleRules,
    BasketWidgetResponse,
    CheckoutResponse,
    HeaderAuthStateResponse,
    OrderResponse,
    UserPanelResponse,
    ModulesApiResponse,
} from './Responses';

import CasinoSliderItem from 'Api/Models/CasinoSliderItem';

class SiteApi extends AbstractSiteApi {
    currentJackpot(): Promise<AxiosResponse> {
        return this.get('current-jackpot');
    }

    faq(onlyPartials: boolean = false): Promise<SiteFaq[]> {
        return this.get(onlyPartials ? 'faq?onlyPartials' : 'faq').then(
            (response: AxiosResponse<GetFaqResponse[]>): SiteFaq[] => {
                return response.data.map((faq) => new SiteFaq(faq));
            }
        );
    }

    faqCategories(): Promise<SiteFaqCategory[]> {
        return this.get('faq-categories').then((response: AxiosResponse<GetFaqResponse[]>): SiteFaqCategory[] => {
            return response.data.map((faqCategory) => new SiteFaqCategory(faqCategory));
        });
    }

    googleMapWithMarkers(mapId: number): Promise<GoogleMap> {
        return this.get(`google_map/markers/${mapId}`).then(
            (response: AxiosResponse<GoogleMap>): GoogleMap => response.data
        );
    }

    news(getAllNews: boolean, page: number = 1) {
        if (getAllNews === false) {
            return this.get('news/limited').then(function (response: AxiosResponse) {
                return {
                    data: response.data,
                    currentPage: null,
                    lastPage: null,
                };
            });
        }

        return this.get(`news?page=${page}`).then(function (response: AxiosResponse) {
            return {
                data: response.data.data,
                currentPage: response.data.current_page,
                lastPage: response.data.last_page,
            };
        });
    }

    getTermsAndConditionsUrl() {
        return this.get('terms-and-conditions-url');
    }

    getPrivacyPolicyUrl() {
        return this.get('privacy-policy-url');
    }

    lotteryList(lotteryTypeFilter: string | null): Promise<AxiosResponse<LotteryListResponse[]>> {
        return this.get('lottery-list', {
            params: {
                lottery_type: lotteryTypeFilter ?? '',
            },
        });
    }

    defaultLottery(): Promise<SiteDefaultLottery> {
        return this.get('lottery/default').then(
            (response: AxiosResponse<SiteDefaultLottery>): SiteDefaultLottery => response.data
        );
    }

    getCountryListRawResponse(): Promise<AxiosResponse> {
        return this.get('country-list');
    }

    getCountryStates(country: string): Promise<AxiosResponse> {
        return this.get(`country-list/states/${country}`);
    }

    getAvailableSocialProviders(): Promise<AxiosResponse> {
        return this.get('social-providers');
    }

    checkIsUserEmailAlreadyExist(data: { email: string; phone: string }): Promise<AxiosResponse> {
        return this.post('register/check', data);
    }

    async getJackpotBannerData(displaySlug: string): Promise<GetJackpotBannerDataResponse> {
        return (await this.get('jackpot-banner-data', { params: { display_slug: displaySlug } })).data;
    }

    async getPlayData(slug: string): Promise<PlayResponse> {
        return (await this.get(`play/${slug}`)).data;
    }

    public getCountryList() {
        return this.get('countries/list').then((response) => response.data);
    }

    async getCasinoSliderItems(sliderSlug: string): Promise<Array<CasinoSliderItem>> {
        return (await this.get(`casino-sliders/${sliderSlug}`)).data;
    }

    async getLatestResultsLimited(): Promise<GetLatestDrawResponse[]> {
        return (await this.get('latest-draws-limited')).data;
    }

    async getLatestWinnersLimited(): Promise<GetLatestWinnersResponse[]> {
        return (await this.get('winners/latest')).data;
    }

    async markAllNotificationsAsRead() {
        return this.get('notifications/mark-all');
    }

    async getActiveNotificationGifts(): Promise<ManualNotificationGiftResponse[]> {
        return (await this.get('notifications/gifts')).data.data;
    }

    async claimNotificationGift(notificationId: number): Promise<ManualNotificationGiftResponse> {
        return (await this.post(`notifications/gifts/${notificationId}/claim`)).data.data;
    }

    async getHeaderMenuItems(): Promise<GetHeaderMenuItemsResponse[]> {
        return (await this.get('menus/header_menu_items')).data;
    }

    public async getBasketWidget(): Promise<BasketWidgetResponse> {
        return (await this.get('basket-widget')).data;
    }

    public removeBasketLine(lineId: string): Promise<AxiosResponse> {
        return this.delete(`basket/lines/${lineId}`);
    }

    public addBasketLine(formData: FormData): Promise<AxiosResponse> {
        return this.post('basket/lines', formData);
    }

    public async getCheckout(): Promise<CheckoutResponse> {
        const config = { params: { time: Date.now() } };

        return (await this.get('checkout', config)).data;
    }

    public async getOrder(id: string): Promise<OrderResponse> {
        return (await this.get(`orders/${id}`)).data;
    }

    public async getLotteryListWithRules(
        lotteryTypeFilter: string | null,
        filteredStateCode: string | null = null
    ): Promise<GetLotteryListWithRules[]> {
        const params: Record<string, string> = {};

        if (typeof lotteryTypeFilter === 'string' && lotteryTypeFilter !== '') {
            params.lottery_type = lotteryTypeFilter;
        }

        if (typeof filteredStateCode === 'string' && filteredStateCode !== '') {
            params.state = filteredStateCode;
        }

        const options = Object.keys(params).length ? { params } : undefined;

        return (await this.get('lottery/rules', options)).data;
    }

    public async getPickListWithRules(): Promise<GetLotteryListWithRules[]> {
        return (await this.get('lottery/pick/rules')).data;
    }

    public async getLotteryWithRulesAndLastDraw(lotterySlug: string): Promise<GetLotteryForSingleRules> {
        const response = await this.get(`lottery/${lotterySlug}`);
        return response.data.lottery;
    }

    public async getPickWithRules(lotterySlug: string): Promise<GetLotteryForSingleRules> {
        const response = await this.get(`lottery/pick/${lotterySlug}`);
        return response.data.lottery;
    }

    public async getUserForAuthPanel(): Promise<UserPanelResponse> {
        return (await this.get('user-panel')).data;
    }

    public async getHeaderAuthState(): Promise<HeaderAuthStateResponse> {
        return (await this.get('header/auth-state')).data;
    }

    public getSliderItems(sliderSlug: string): Promise<SliderItem[]> {
        return this.get(`slider-items/${sliderSlug}`).then((response: AxiosResponse<GetSliderItems>) =>
            response.data.map((item) => new SliderItem(item))
        );
    }

    async getLootboxData(slug: string): Promise<LootboxDataResponse> {
        return (await this.get(`lootbox/${slug}/data`)).data;
    }

    async getLootboxes(): Promise<Lootbox[]> {
        const response = (await this.get('lootbox')).data;

        if (!Array.isArray(response)) {
            return [];
        }

        return response
            .map((entry: { lootbox?: Lootbox } | Lootbox) => ('lootbox' in entry && entry.lootbox ? entry.lootbox : entry))
            .filter((lootbox: Lootbox | null | undefined): lootbox is Lootbox => lootbox !== null && lootbox !== undefined);
    }

    private async refreshCsrfToken(): Promise<string | undefined> {
        try {
            const res = await axios.get('/csrf/refresh', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            const newToken: string | undefined = res?.data?.token;
            if (newToken) {
                // Set default header for subsequent requests
                window.axios.defaults.headers.common['X-CSRF-TOKEN'] = newToken;
                // Update meta tag as well
                const meta = document.head.querySelector('meta[name="csrf-token"]');
                if (meta) meta.setAttribute('content', newToken);
            }
            return newToken;
        } catch (_e) {
            return undefined;
        }
    }

    async openLootbox(slug: string): Promise<AxiosResponse<LootboxOpenResponse>> {
        try {
            return (await this.post(`lootbox/${slug}/open`)).data;
        } catch (error: any) {
            const isCsrfMismatch =
                (error?.response && error.response.status === 419) ||
                /CSRF token mismatch/i.test(error?.response?.data?.message ?? '');
            if (isCsrfMismatch) {
                const newToken = await this.refreshCsrfToken();
                if (newToken) {
                    return (await this.post(`lootbox/${slug}/open`)).data;
                }
            }
            throw error;
        }
    }

    async redeemLootboxCode(code: string): Promise<LootboxRedeemResponse> {
        return (await this.post('lootbox/redeem', { code })).data;
    }

    async openLootboxTicket(token: string): Promise<AxiosResponse<LootboxOpenResponse>> {
        return (await this.post(`lootbox/ticket/${token}/open`)).data;
    }

    async getLootboxLatestWins(limit: number = 20): Promise<LootboxWinEntry[]> {
        return (await this.get('lootbox/latest-wins', { params: { limit } })).data;
    }

    public async getBundles(): Promise<Bundle[]> {
        return (await this.get('bundles')).data;
    }

    public async getLotteryDataForPlayWidget(lotterySlug: string): Promise<LotteryListResponse> {
        const response = await this.get(`lottery/play-widget/${lotterySlug}`);
        return response.data.lottery;
    }

    public async getLotteryDataForPlaySliderWidget(): Promise<LotteryListResponse[]> {
        const response = await this.get('lottery/play-slider-widget');
        return response.data.lotteries;
    }

    public async getCurrencies() {
        const response = await this.get('currencies');
        return response.data.currencies;
    }

    public async hasPromoCode(usePlace: 'purchase' | 'deposit' | 'register'): Promise<boolean> {
        const response = await this.get('promo-code/exists', {
            params: { usePlace },
        });
        return response.data.hasPromoCode === true;
    }

    public async getGameCategories(): Promise<GetAllCasinoGameCategoryResponse> {
        return (await this.get('game-categories')).data;
    }

    public async getGameBlocks(): Promise<GetAllCasinoGameCategoryBlockResponse> {
        return (await this.get('game-category-blocks')).data;
    }

    public getModules(): Promise<ModulesApiResponse> {
        return this.get('modules').then((response: AxiosResponse) => response.data);
    }
}

export default new SiteApi();

export type ManualNotificationGiftResponse = Readonly<{
    id: number;
    label: string;
    type: string | null;
    value: string | null;
    cta: string;
    reason: string | null;
    status: string | null;
    expires_at: string | null;
    url: string;
}>;
